import React, { useMemo, useState } from 'react'
import { Button, Grid2, Typography } from '@mui/material'
import CustomDivider from '../../shared/utils/CustomDivider'
import { Formik, Field, Form } from "formik";
import * as yup from "yup";
import { TextFormField } from '../../shared/components/FormFields/TextFormField'
import { isValidPhoneNumber } from "react-phone-number-input";
import { PhoneInputFormField } from '../../shared/components/FormFields/PhoneInputFormField'
import CitiesData from './../../Data/Citiescopy.json';
import StatesData from './../../Data/States.json';
import { useMutation } from '@tanstack/react-query';
import api from '../../assets/api/usePostApi';
import { AutoCompleteField } from '../../shared/components/FormFields/AutoCompleteField';
import { toast } from 'react-toastify';
import { latitudeValidation, longitudeValidation } from './SchemaLatLong';

const Individual = () => {
    const [cityOptions, setCityOptions] = useState([]);
    const StateOption = useMemo(() => {
        return StatesData.map((location) => ({
            id: location.id,
            label: location.name,
            value: location.name,
        })) || [];
    }, [StatesData]);
    const validationSchema = yup.object().shape({
        first_name: yup.string().required("First Name is required"),
        last_name: yup.string().required("Last of Flats is required"),
        address: yup.string().required("Address is required"),
        city_id: yup.string().required("City is required"),
        state_id: yup.string().required("State is required"),
        country_id: yup.string().required("Country is required"),
        pincode: yup.number().required("Pincode is required"),
        latitude: latitudeValidation,
        longitude:longitudeValidation,
        mobile_no: yup.string().test(
            "is-phone",
            "Phone number is not valid",
            (value) => value && isValidPhoneNumber(value)
        ).required("Mobile Number is required"),
        email: yup.string().email("Invalid email").required("E-Mail is required")
    });


    const initialValues = {
        first_name: "",
        last_name: "",
        address: "",
        street: "",
        landmark: "",
        city_id: "",
        state_id: "",
        country_id: "India",
        pincode: "",
        latitude: "",
        longitude: "",
        mobile_no: "",
        email: ""
    };

    const mutation = useMutation({
        mutationFn: (formData) => api.post("collab-individuals-on-board/create", formData), // No need to repeat the full URL
    });

    const handleSubmit = (values, { setSubmitting, resetForm }) => {
        const ojb = {
            ...values,
            designation: values.designation || ""
        };
        mutation.mutate(ojb, {
            onSuccess: (response) => {
                if (response?.data) {
                    toast.success(` ${response.data.message}`, {
                        position: "top-right",
                        autoClose: 3000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        theme: "light",
                    });
                } else {
                    toast.warning("Message sent, but no response from server.", {
                        position: "top-right",
                        autoClose: 3000,
                        theme: "light",
                    });
                }

                resetForm();
            },
            onError: (error) => {
                console.error("Error Response:", error.response);
                toast.error(`Error sending message: ${error.response?.data?.message || error.message}`, {
                    position: "top-right",
                    autoClose: 5000,
                    theme: "dark",
                });
            },
            onSettled: () => {
                setSubmitting(false);
            }
        });
    };
    return (
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
            {({ setFieldValue }) => (
                <Form>

                    <Grid2 container spacing={5}>
                        <Grid2 children size={{ xs: 12, sm: 6 }}>
                            <Field
                                name="first_name"
                                component={TextFormField}
                                label="First Name"
                                isRequired={true}
                            />
                        </Grid2>
                        <Grid2 children size={{ xs: 12, sm: 6 }}>
                            <Field
                                name="last_name"
                                component={TextFormField}
                                label="Last Name"
                                isRequired={true}
                            />
                        </Grid2>
                        <Grid2 children size={12}>
                            <Field
                                name="address"
                                component={TextFormField}
                                label="Address"
                                isRequired={true}
                            />
                        </Grid2>
                        <Grid2 children size={{ xs: 12, sm: 6 }}>
                            <Field
                                name="street"
                                component={TextFormField}
                                label="Street"
                                isRequired={true}
                            />
                        </Grid2>
                        <Grid2 children size={{ xs: 12, sm: 6 }}>
                            <Field
                                name="landmark"
                                component={TextFormField}
                                label="Landmark"
                                isRequired={true}
                            />
                        </Grid2>
                        <Grid2 children size={{ xs: 12, sm: 6 }}>
                            <Field
                                name="country_id"
                                component={AutoCompleteField}
                                options={[{
                                    id: 101,
                                    label: "India",
                                    value: "India",
                                }]}
                                label="Country"
                                isRequired
                            />
                        </Grid2>

                        <Grid2 children size={{ xs: 12, sm: 6 }}>
                            <Field
                                name="state_id"
                                component={AutoCompleteField}
                                options={StateOption}
                                label="State"
                                isRequired
                                onValueChange={(newValue) => {
                                    const selectedStateId = newValue?.id || "";
                                    setFieldValue("state_id", selectedStateId);
                                    setFieldValue("city_id", "");
                                    const stateData = CitiesData.find(item => item.id === selectedStateId);
                                    const cities = stateData ? stateData.cities : [];
                                    setCityOptions(
                                        cities.map(location => ({
                                            id: location.id,
                                            label: location.name,
                                            value: location.name
                                        }))
                                    );
                                }}
                            />
                        </Grid2>
                        <Grid2 children size={{ xs: 12, sm: 6 }}>
                            <Field
                                name="city_id"
                                component={AutoCompleteField}
                                options={cityOptions}
                                label="City"
                                isRequired
                                onChange={(newValue) => {
                                    setFieldValue("city_id", newValue?.id || "");
                                }}
                            />
                        </Grid2>
                        <Grid2 children size={{ xs: 12, sm: 6 }}>
                            <Field
                                name="pincode"
                                component={TextFormField}
                                label="Pincode"
                                isRequired={true}
                                type={"number"}
                            />
                        </Grid2>
                        <Grid2 children size={{ xs: 12, sm: 6 }}>
                            <Field
                                name="latitude"
                                component={TextFormField}
                                label="Latitude"
                                isRequired={true}
                                type={"number"}
                            />
                        </Grid2>
                        <Grid2 children size={{ xs: 12, sm: 6 }}>
                            <Field
                                name="longitude"
                                component={TextFormField}
                                label="Longitude"
                                isRequired={true}
                                type={"number"}
                            />
                        </Grid2>
                    </Grid2>

                    <CustomDivider />
                    <Grid2 container spacing={5}>

                        <Grid2
                            size={12}
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr auto",
                                alignItems: "start"
                            }}
                        >
                            <Typography variant='h6' fontSize={"18px"} fontWeight={600}>
                                Contact Details
                            </Typography>

                        </Grid2>
                        <Grid2 children size={{ xs: 12, sm: 6 }}>
                            <Field
                                name="mobile_no"
                                component={PhoneInputFormField}
                                label="Mobile Number"
                                isRequired={true}
                            />
                        </Grid2>
                        <Grid2 children size={{ xs: 12, sm: 6 }}>
                            <Field
                                name="email"
                                component={TextFormField}
                                label="E-Mail"
                                isRequired={true}
                            />
                        </Grid2>
                    </Grid2>
                    <CustomDivider />
                    <Grid2 container>
                        <Grid2 children size={{ xs: 12, sm: 6 }} />
                        <Grid2 children size={{ xs: 12, sm: 6 }}>
                            <Button variant='contained' fullWidth type='submit'>Submit</Button>
                        </Grid2>
                    </Grid2>
                </Form>
            )}
        </Formik>
    )
}

export default Individual